/**
 * body.js --- Main body of the archive
 *
 * The body is made up of all the content below the header and
 * the nav/meadia board. It includes the article/editorial heading (Title),
 * the main text, and the bio/editor signature (Footer). It also
 * contains its own independent modular styled components
 */

import React from "react"
import * as Nuclear from "../nuclear"

import Title from "./title"
import Footer from "./footer"

import Styled from "styled-components"

/**
 * Body (Default): All the content for an article
 * or editorial's main writing
 */
const Body = props => {
  /**
   * mainBodyContent (const)
   *
   * All of the props from the graphQL query are
   * passed through the nuclear option in order to parse rich text
   * files in the correct format, either by the default editorial and
   * article options, or with special outlier options
   */
  const mainBodyContent = Nuclear.Option(props)

  return (
    <MainArticleContent contentType={mainBodyContent.contentType}>
      <ArticleTextWrapper contentType={mainBodyContent.contentType}>
        <Title
          title={mainBodyContent.title}
          subtitle={mainBodyContent.subtitle}
          author={mainBodyContent.author}
          date={mainBodyContent.date}
        />
        {getDropcap(
          mainBodyContent.content,
          mainBodyContent.contentType,
          mainBodyContent.color,
          mainBodyContent.dropcap
        )}
        <MainArticleText>
          <main>
            {getArticleContent(
              mainBodyContent.content,
              mainBodyContent.dropcap
            )}
          </main>
          <Footer
            contentType={mainBodyContent.contentType}
            content={mainBodyContent.footer}
            headshots={mainBodyContent.headshots}
          />
        </MainArticleText>
      </ArticleTextWrapper>
    </MainArticleContent>
  )
}

export default Body

/* ===================================================*/

/**
 * MainArticleContent: Styled component for
 * article content.
 *
 * CSS Variables: border-top-color, background color
 */
const MainArticleContent = Styled.div`
    position: relative;
    border-top: 2px solid;
    overflow: hidden;
    border-top-color: ${props =>
      props.contentType === "Editorial" ? "#FFFFFF" : "#000000"};
    background-color: ${props =>
      props.contentType === "Editorial" ? "#000000" : "#FFFFFF"};
    width: 100vw;
    height: auto;
    top: 45vmax;
    padding-bottom: 2%;

  /**
   * iPads
   */
  @media only screen and (min-width: 768px) and (max-width: 1024px) {
    top: 55vw;
  }

  /**
   * iPhones
   */
  @media only screen and (max-width: 767px) {
    top: 50vw;
  }
`

/**
 * ArticleTextWrapper: Wrapper that
 * is responsible for some styling
 * in the main article content
 *
 * CSS Variables: color
 */
const ArticleTextWrapper = Styled.div`
    font-family: Didot;
    color: ${props =>
      props.contentType === "Editorial" ? "#FFFFFF" : "#000000"};
    position: relative;
    left: 15vw;
    width: 70vw;
    height: 100%;
    font-size: 1.8vw;

  /**
   * iPads [Header text size]
   */
  @media only screen and (min-width: 768px) and (max-width: 1024px) {
    font-size: 2.5vw;
  }

  /**
   * iPhones [Header text size]
   */
  @media only screen and (max-width: 767px) {
    font-size: 3.5vw;
    left: 10vw;
    width: 75vw;
  }
`

/**
 * MainArticleText: Formatting for
 * the article's rich text
 */
const MainArticleText = Styled.div`
  font-size: 1.45vw;
  p {
    text-align: justify;
  }

  /**
   * iPads [Main text size]
   */
  @media only screen and (min-width: 768px) and (max-width: 1024px) {
    font-size: 2vw;
  }

  /**
   * iPhones [Main text size, justification]
   */
  @media only screen and (max-width: 767px) {
    font-size: 4.3vw;
    p {
      text-align: left;
    }
  }
`

/**
 * Dropcap: Styled component for
 * dropcap
 *
 * CSS Variables: border-color, color
 */
const Dropcap = Styled.div`
  float: left;
  position: relative;
  margin-top: 2.3vmin;
  margin-bottom: -0.2vh;
  margin-right: 1.5vmin;
  margin-left: 0.2vmax;
  padding: 0% 1.7% 0% 1.7%;
  font-size: 5vw;
  line-height: normal;
  border: 1px solid;
  border-color: ${props =>
    props.contentType === "Editorial" ? "#FFFFFF" : props.color};
  color: ${props =>
    props.contentType === "Editorial" ? "#FFFFFF" : props.color};

   /**
   * iPads 
   */
  @media only screen and (min-width: 768px) and (max-width: 1024px) {
    font-size: 7vw;
    padding: 0% 2% 0% 2%;
    margin-top: 1.7vmin;
    margin-right: 1.5vmin;
  }

  /**
   * iPhones
   */
  @media only screen and (max-width: 767px) {
    font-size: 15vw;
    padding: 0% 3% 0% 3%;
    margin-top: 5vmin;
    margin-right: 2.5vmin;

  }
`

/* ===================================================*/

/**
 * getDropcap: Helper function that returns first character
 * of rich text JSON
 */
function getDropcap(articleContent, contentType, color, dropcapFlag) {
  let index = 0
  // Cycle through rich text JSON until first paragraph
  while (articleContent[index].type !== "p") {
    index++
  }
  // Check for empty strings
  if (articleContent[index].props.children[0] === "") {
    index++
  }

  if (dropcapFlag) {
    return (
      <Dropcap contentType={contentType} color={color}>
        {articleContent[index].props.children[0].charAt(0)}
      </Dropcap>
    )
  } else return null
}

/**
 * getArticleContent: Helper function that returns the
 * rich text JSON content without the first character
 */
function getArticleContent(articleContent, dropcapFlag) {
  let index = 0
  // Cycle through rich text JSON until first paragraph
  while (articleContent[index].type !== "p") {
    index++
  }
  // Check for empty strings
  if (articleContent[index].props.children[0] === "") {
    index++
  }

  if (dropcapFlag) {
    articleContent[index].props.children[0] = articleContent[
      index
    ].props.children[0].substr(1)
    return articleContent
  } else return articleContent
}
